/**
 * Pointer Events 唯一入口 —— 对应技术设计 v1.4 §12.4.2「一条 Pointer Events 路径」。
 *
 * 只有这个文件碰 DOM 事件。它把浏览器事件翻译成世界坐标，交给 drag-controller
 * 这个纯状态机处理，再把结果交回上层。**不写 mouse / touch 两套监听。**
 *
 * 关键细节：
 * - `setPointerCapture` 让手指滑出器材范围后仍然收得到 move（触屏尤其重要）
 * - `touch-action: none` 只加在器材热区（CSS 侧），不加容器，否则页面滚不动
 * - 长按 350ms 显示端口标签，代偿触屏没有 hover 的问题
 */

import type { AssemblyGraph } from "../eom/assembly-graph.js";
import type { Point } from "../eom/port.js";
import {
  beginDrag,
  cancelDrag,
  endDrag,
  moveDrag,
  type DragEndResult,
  type DragMoveResult,
  type DragSession,
} from "./drag-controller.js";
import {
  detectPointerClass,
  profileFor,
  type InputProfile,
  type PointerClass,
} from "./input-profile.js";
import type { SnapContext } from "./snap-controller.js";

export interface PointerHooks {
  /** 取当前图。每次事件现取，避免控制器持有过期引用 */
  getGraph(): AssemblyGraph;
  /** 图变更后写回上层 */
  setGraph(graph: AssemblyGraph): void;
  /** 当前 Mission 期望的关系（§5.3 任务维度），可选 */
  getDesiredRelationKeys?(): ReadonlySet<string> | undefined;
  onDragStart?(objectId: string): void;
  onDragMove?(result: DragMoveResult, session: DragSession): void;
  onDragEnd?(result: DragEndResult, session: DragSession): void;
  /** 长按：显示该器材的端口标签（触屏 hover 代偿） */
  onLongPress?(objectId: string): void;
  /** 点击（未越过起手阈值） */
  onTap?(objectId: string): void;
}

/** SVG 客户端坐标 → viewBox 世界坐标。缩放/留白全由 SVG 自己的 CTM 处理。 */
function toWorldPoint(svg: SVGSVGElement, event: PointerEvent): Point {
  const point = svg.createSVGPoint();
  point.x = event.clientX;
  point.y = event.clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return { x: event.clientX, y: event.clientY };
  const mapped = point.matrixTransform(ctm.inverse());
  return { x: mapped.x, y: mapped.y };
}

function objectIdAt(target: EventTarget | null): string | null {
  if (!(target instanceof Element)) return null;
  const group = target.closest("[data-object-id]");
  return group instanceof HTMLElement || group instanceof SVGElement
    ? (group.dataset.objectId ?? null)
    : null;
}

export class PointerController {
  private svg: SVGSVGElement | null = null;
  private session: DragSession | null = null;
  private activePointerId: number | null = null;
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private profile: InputProfile = profileFor(detectPointerClass());

  private readonly onDown = (event: PointerEvent): void => this.handleDown(event);
  private readonly onMove = (event: PointerEvent): void => this.handleMove(event);
  private readonly onUp = (event: PointerEvent): void => this.handleUp(event);
  private readonly onCancel = (event: PointerEvent): void => this.handleCancel(event);

  constructor(private readonly hooks: PointerHooks) {}

  attach(svg: SVGSVGElement): void {
    this.detach();
    this.svg = svg;
    svg.addEventListener("pointerdown", this.onDown);
    svg.addEventListener("pointermove", this.onMove);
    svg.addEventListener("pointerup", this.onUp);
    svg.addEventListener("pointercancel", this.onCancel);
  }

  detach(): void {
    const svg = this.svg;
    if (!svg) return;
    svg.removeEventListener("pointerdown", this.onDown);
    svg.removeEventListener("pointermove", this.onMove);
    svg.removeEventListener("pointerup", this.onUp);
    svg.removeEventListener("pointercancel", this.onCancel);
    this.clearLongPress();
    this.svg = null;
    this.session = null;
    this.activePointerId = null;
  }

  getPointerClass(): PointerClass {
    return this.profile.pointerClass;
  }

  private context(): SnapContext {
    const desired = this.hooks.getDesiredRelationKeys?.();
    return {
      graph: this.hooks.getGraph(),
      profile: this.profile,
      ...(desired !== undefined ? { desiredRelationKeys: desired } : {}),
    };
  }

  private clearLongPress(): void {
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  private handleDown(event: PointerEvent): void {
    if (!this.svg || this.session) return;
    const objectId = objectIdAt(event.target);
    if (!objectId) return;

    // 档位按**这一次操作**用的是什么定：同一台设备可能既有触屏又有鼠标
    this.profile = profileFor(detectPointerClass(event));

    const started = beginDrag(this.hooks.getGraph(), objectId, toWorldPoint(this.svg, event));
    if (!started) return;

    this.session = started.session;
    this.activePointerId = event.pointerId;
    this.hooks.setGraph(started.graph);
    this.hooks.onDragStart?.(objectId);

    try {
      this.svg.setPointerCapture(event.pointerId);
    } catch {
      // 某些环境（jsdom / 已释放的指针）不支持捕获，退化为普通监听即可
    }

    this.clearLongPress();
    this.longPressTimer = setTimeout(() => {
      if (this.session && !this.session.moved) this.hooks.onLongPress?.(objectId);
    }, this.profile.longPressMs);
  }

  private handleMove(event: PointerEvent): void {
    const session = this.session;
    if (!this.svg || !session || event.pointerId !== this.activePointerId) return;

    const result = moveDrag(
      this.hooks.getGraph(),
      session,
      toWorldPoint(this.svg, event),
      this.context(),
    );
    if (session.moved) this.clearLongPress();
    this.hooks.setGraph(result.graph);
    this.hooks.onDragMove?.(result, session);
  }

  private handleUp(event: PointerEvent): void {
    const session = this.session;
    if (!session || event.pointerId !== this.activePointerId) return;

    const result = endDrag(this.hooks.getGraph(), session, this.context(), Date.now());
    this.finish(event, session);
    this.hooks.setGraph(result.graph);
    if (result.wasClick) this.hooks.onTap?.(session.objectId);
    this.hooks.onDragEnd?.(result, session);
  }

  private handleCancel(event: PointerEvent): void {
    const session = this.session;
    if (!session || event.pointerId !== this.activePointerId) return;
    const graph = cancelDrag(this.hooks.getGraph(), session);
    this.finish(event, session);
    this.hooks.setGraph(graph);
  }

  private finish(event: PointerEvent, _session: DragSession): void {
    this.clearLongPress();
    try {
      this.svg?.releasePointerCapture(event.pointerId);
    } catch {
      // 同 setPointerCapture：不支持就算了
    }
    this.session = null;
    this.activePointerId = null;
  }
}
